// Render wrapper used by ListPlugin's `render.belowNodes`. Plate's v53 list
// model is indent-based: any block can carry `listStyleType` + `indent` and
// this wrapper paints the surrounding <ul>/<ol> + <li>.
// Simplified from Plate's reference BlockList (todo/check items deferred).
import React, { useMemo } from 'react';
import { useEditorRef } from 'platejs/react';

export const BlockList: any = (props: any) => {
    if (!props.element?.listStyleType) return undefined;
    return (innerProps: any) => <List {...innerProps} />;
};

const ORDERED = ['decimal', 'decimal-leading-zero', 'lower-roman', 'upper-roman', 'lower-alpha', 'upper-alpha'];
const BULLET_CYCLE = ['disc', 'circle', 'square'];
const NUMBERED_CYCLE = ['decimal', 'lower-alpha', 'lower-roman'];

function isOrderedListStyle(listStyleType: string) {
    return ORDERED.includes(listStyleType);
}

// Largest `fontSize` leaf mark in the block. FontSizePlugin stores font size as
// an inline mark on the text leaves, so the <li>/::marker (sized in em against
// the <li>) never see it — bumping the text size left the bullet/number pinned
// at the default size. We read it off the block and apply it to the <li> below
// so the native marker scales with the text. Returns undefined when no leaf
// carries an explicit size (default body size → default marker).
//
// Walks into inline element children too (e.g. a link whose child text leaf
// carries the fontSize), so the marker still scales when the item's text lives
// inside an inline rather than as a direct leaf.
export function blockFontSize(element: any): string | undefined {
    let maxPx = 0;
    let raw: string | undefined;
    const visit = (node: any): void => {
        const fs = typeof node?.fontSize === 'string' ? node.fontSize : undefined;
        if (fs) {
            const px = parseFloat(fs);
            if (!Number.isNaN(px) && px > maxPx) {
                maxPx = px;
                raw = fs;
            }
        }
        if (Array.isArray(node?.children)) for (const child of node.children) visit(child);
    };
    if (Array.isArray(element?.children)) for (const child of element.children) visit(child);
    return raw;
}

// Cycle the marker style by indent depth so nested levels read as a
// hierarchy — disc → circle → square for bullets, 1. → a. → i. for numbers.
// The stored listStyleType stays as-is on the node; we only swap the
// RENDERED marker. Avoids touching the save format.
function styleByIndent(base: string, indent: number | undefined): string {
    const depth = Math.max(0, (indent ?? 1) - 1);
    if (BULLET_CYCLE.includes(base)) {
        return BULLET_CYCLE[depth % BULLET_CYCLE.length];
    }
    if (NUMBERED_CYCLE.includes(base)) {
        return NUMBERED_CYCLE[depth % NUMBERED_CYCLE.length];
    }
    return base;
}

// Ordinal of an ordered-list item within its run of consecutive sibling list
// items at the SAME indent + SAME list style, given the item's own index and
// its siblings array. Plate's indent model renders each item as its own
// single-item <ol>, so without an explicit `start` every item restarts at 1
// (the "1. 1. 1." bug). We can't rely on the normalizer-provided `listStart`
// (it arrives undefined on freshly-loaded templates), so derive the number
// directly from the document position. Walking-back rules:
//   • non-list block, or a shallower list item → the run ended (restart)
//   • deeper-indented item → a nested sub-list, skip it (doesn't affect this level)
//   • same indent + same style → counts toward the ordinal
//   • a `listRestart` on the item itself, or on a counted sibling met while
//     walking back → that value pins the run's own count (RECUR-27303 §2.1)
//
// Pure (no editor dependency) so it can be shared with the plateToHtml
// serializer, which walks its own flat node array rather than a live editor
// tree — see plateToHtml.ts's positional <ol start> derivation.
//
// `listRestart` is plate's own persisted prop (@platejs/utils KEYS.listRestart)
// — populated at import time by stampWordListRestarts only where Word's real
// ordinal diverges from this positional count. No saved document carries it
// today, so these two early-outs are unreachable for any existing document
// (§7.3): behaviour is byte-identical to before this change until a document
// actually has one.
export function computeOrderedStartFromSiblings(
    siblings: any[] | undefined,
    idx: number,
    listStyleType: string,
    indent: number | undefined,
): number | undefined {
    if (!Array.isArray(siblings)) return undefined;
    const curIndent = indent ?? 1;
    const self = siblings[idx];
    if (typeof self?.listRestart === 'number' && self.listRestart > 0) return self.listRestart;
    let n = 1;
    for (let i = idx - 1; i >= 0; i--) {
        const sib = siblings[i];
        const sibStyle = sib?.listStyleType;
        const sibIndent = sib?.indent ?? 1;
        if (!sibStyle) break; // a non-list block ends the run
        if (sibIndent < curIndent) break; // shallower list item closes this level
        if (sibIndent > curIndent) continue; // deeper nested item — doesn't count here
        if (sibStyle !== listStyleType) break; // different list style at this level
        if (typeof sib?.listRestart === 'number' && sib.listRestart > 0) return sib.listRestart + n;
        n++;
    }
    return n;
}

// Stamp @platejs/list's `listStart` convention onto a loaded document value:
// an ordered item carries listStart === its positional ordinal for items 2+,
// and NO listStart on a run's first item (the plugin's normalizeListStart
// unsets an explicit 1, so stamping one would only churn). Docs composed
// before polus emitted listStart have none at all; on Enter the plugin then
// derives the new item's number as (prev.listStart ?? 1) + 1 and lands on 2
// mid-run — the "item 5, next number 2" bug. Normalization only visits dirty
// paths, so the repair has to happen at load, not in normalizeNode (an
// always-on positional rule there fights normalizeListStart's unset-when-1
// and never converges). Recurses into containers so items inside table cells
// are stamped too. Returns the input array when nothing changed.
export function stampOrderedListStarts(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return nodes;
    const out = nodes.map((node) => {
        if (!Array.isArray(node?.children) || node.children.length === 0) return node;
        const children = stampOrderedListStarts(node.children);
        return children === node.children ? node : { ...node, children };
    });
    let changed = out.some((n, i) => n !== nodes[i]);
    for (let i = 0; i < out.length; i++) {
        const node = out[i];
        const style = node?.listStyleType;
        if (!style || !isOrderedListStyle(style)) continue;
        const ordinal = computeOrderedStartFromSiblings(out, i, style, node.indent);
        if (ordinal == null) continue;
        if (ordinal === 1) {
            if (node.listStart != null) {
                const { listStart: _ls, ...rest } = node;
                out[i] = rest;
                changed = true;
            }
        } else if (node.listStart !== ordinal) {
            out[i] = { ...node, listStart: ordinal };
            changed = true;
        }
    }
    return changed ? out : nodes;
}

// Editor-tree adapter: locates `element`'s index + siblings via the live
// editor API, then delegates to the pure walk above.
function computeOrderedStart(editor: any, element: any, listStyleType: string, indent: number | undefined): number | undefined {
    try {
        const path = editor?.api?.findPath?.(element);
        if (!Array.isArray(path) || path.length === 0) return undefined;
        const idx = path[path.length - 1];
        const parent = editor?.api?.node?.(path.slice(0, -1))?.[0];
        return computeOrderedStartFromSiblings(parent?.children, idx, listStyleType, indent);
    } catch {
        return undefined;
    }
}

const List = React.memo(function List(props: any) {
    const editor = useEditorRef();
    const { listStart, listStyleType, indent, lineHeight, align } = props.element;
    const ordered = isOrderedListStyle(listStyleType);
    const effectiveStyle = styleByIndent(listStyleType, indent);
    const Tag = ordered ? 'ol' : 'ul';

    // Prefer a normalizer-provided listStart; otherwise derive the ordinal from
    // the item's position so numbered lists count 1, 2, 3 … instead of 1, 1, 1.
    // useMemo: computeOrderedStart calls editor.api.findPath (O(doc)) — cache it
    // so it only recomputes when this element node or the editor reference changes.
    // When a sibling edits, props.element identity is stable → cached value reused.
    // When this node itself changes (indent/style/position), element identity
    // changes → recomputes. listStart from the normalizer short-circuits the memo.
    const derivedStart = useMemo(
        () => (ordered && listStart == null ? computeOrderedStart(editor, props.element, listStyleType, indent) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editor, props.element, listStyleType, indent, ordered],
    );
    const orderedStart = ordered ? listStart ?? derivedStart : undefined;

    // Propagate LineHeightPlugin's `lineHeight` block prop onto the <ul>/<li>
    // chrome so the marker (rendered against the <li>'s line-box) shifts in
    // step with the text inside the <p>. Without this, Plate's inject only
    // styles the inner <p>, leaving the bullet pinned to a 1.2 line-box even
    // when the user picks 2.
    const ulStyle: React.CSSProperties = { listStyleType: effectiveStyle, padding: 0 };
    const liStyle: React.CSSProperties = {};
    if (lineHeight != null) {
        ulStyle.lineHeight = String(lineHeight);
        liStyle.lineHeight = String(lineHeight);
    }

    // Ordered lists paint their number via a CSS counter (`::marker` is
    // suppressed in PlateEditor.module.scss) — Chromium renders `::marker`
    // counter GLYPHS (unlike disc/circle) at an internal size/baseline that
    // doesn't track the sibling text's font metrics, no matter what font-size
    // / line-height / font-family is set on the marker itself. The visible
    // symptom: "1." "2." render oversized and shifted below the text
    // baseline. Painting the number as `::before` content pins it to the
    // same line-box as the text (same fix already applied to the `square`
    // bullet glyph below). `counter-reset` seeds the running count one below
    // the desired start so the first `counter-increment` in CSS lands on
    // orderedStart; the `start` attribute is kept for a11y (list-item
    // semantics / assistive tech still read the correct ordinal).
    if (ordered) {
        (ulStyle as any).counterReset = `plate-ordered-list ${(orderedStart ?? 1) - 1}`;
    }

    // Scale the bullet/number marker with the text. The marker is sized in `em`
    // against the <li>; putting the block's font size on the <li> makes the
    // native ::marker grow/shrink in step (the inner <p>'s own fontSize leaf
    // marks still control the text itself).
    const fontSize = blockFontSize(props.element);
    if (fontSize) {
        liStyle.fontSize = fontSize;
    }

    // Make the marker move with the text on right/center/justify aligned
    // items. Default `list-style-position: outside` keeps the bullet in a
    // gutter to the left of the <li>; switching to `inside` makes it part
    // of the content flow, and applying `text-align` on the <li> + inlining
    // the <p> brings marker + text together to the chosen edge.
    if (align && align !== 'left') {
        liStyle.textAlign = align;
        liStyle.listStylePosition = 'inside';
    }

    // Only tag `data-align` for non-left alignments. The PDF override's
    // `li[data-align] { list-style-position: inside }` rule otherwise pulls
    // the marker into the content flow for explicitly-left-aligned blocks
    // too, which manifested as bullets shifting away from the outside
    // gutter in the exported PDF even when the user hadn't asked for it.
    const dataAlign = align && align !== 'left' ? align : undefined;
    return (
        <Tag style={ulStyle} start={orderedStart}>
            <li style={liStyle} data-align={dataAlign}>
                {props.children}
            </li>
        </Tag>
    );
});
